import React from 'react';
import { Badge } from '@mui/material';
import verificationBadge from '../../asset/images/icons/verified_badge.svg';
import Avatar from '@mui/material/Avatar';
import { SxProps, Theme, styled } from '@mui/material/styles';
import { ITopUser, IUserSummary } from '../../types/global/helper';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { ROUTES } from '../../types/global/routes.types';
import { useDispatch } from 'react-redux';
import { navigate } from '../../redux/navigator/action';
import { IUserProfile } from '../../redux/user/actions';
import { avatar_female, avatar_male } from '../../asset/images';

const SmallAvatar = styled(Avatar)(({ theme }) => ({
    width: 20,
    height: 20,
    zIndex: 2,
}));

export interface IAvatar {
    user: IUserSummary | ITopUser | IUserProfile | any;
    sx?: SxProps<Theme>;
    onClick?: (user?: IUserSummary | ITopUser | IUserProfile) => void;
    enableDefaultNavigation?: boolean;
    badgeSx?: SxProps<Theme>;
    /** Use `rectangular` when the avatar is a rounded square so the verified badge aligns cleanly */
    badgeOverlap?: 'circular' | 'rectangular';
    disableVerified?: boolean;
    size?: 'large' | 'medium' | 'small' | 'xLarge';
    withMonogram?: boolean;
}

function stringToColor(string: string) {
    let hash = 0;
    let i;
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
}

/**
 * Safely build initials from a name string.
 */
function getInitials(name: string) {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0) || '';
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`;
}

/**
 * Returns props for a colored monogram avatar with dynamic font size.
 */
function stringAvatar(name: string, fontSize: number, sx?: SxProps<Theme>) {
    return {
        sx: {
            bgcolor: stringToColor(name),
            fontSize,
            ...sx,
        },
        children: getInitials(name),
    };
}

const ThspianAvatar: React.FC<IAvatar> = ({
    user,
    sx,
    onClick,
    enableDefaultNavigation,
    badgeSx,
    badgeOverlap = 'circular',
    disableVerified,
    size = 'large',
    withMonogram,
}) => {
    const upperCaseFirstName = user?.firstName?.toUpperCase() || '';
    const upperCaseLastName = user?.lastName?.toUpperCase() || '';
    const dispatch = useDispatch();
    const selectedAvatar = user?.gender === 'female' ? avatar_female : avatar_male;
    const avatar = !withMonogram ? selectedAvatar : undefined;

    const handleUserIconClicked = () => {
        if (user?.userName) {
            const profileRoute = replaceRouteParam(ROUTES.ESMY_PROFILE, 'profileId?', user.userName);
            dispatch(navigate({ routes: profileRoute }));
        }
    };

    const handleOnClick = () => {
        if (enableDefaultNavigation) {
            handleUserIconClicked();
        } else {
            onClick?.(user);
        }
    };

    // Map size -> dimensions + monogram font size
    const getAvatarDimensions = () => {
        const base = 40; // large default
        switch (size) {
            case 'medium':
                return { width: base * 0.8, height: base * 0.8, fontSize: 12 };
            case 'small':
                return { width: base * 0.5, height: base * 0.5, fontSize: 8 };
            case 'xLarge':
                return { width: base * 2, height: base * 2, fontSize: 16 };
            default: // large
                return { width: base, height: base, fontSize: 14 };
        }
    };

    const avatarSizeStyle = getAvatarDimensions();

    const composedName = `${upperCaseFirstName} ${upperCaseLastName}`.trim();

    return (
        <Badge
            overlap={badgeOverlap}
            sx={badgeSx}
            onClick={handleOnClick}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
                user?.verified && !disableVerified && <SmallAvatar alt="verification" src={verificationBadge} />
            }>
            <Avatar
                alt={composedName}
                src={user?.profile_picture || avatar}
                {...stringAvatar(composedName || user?.userName || 'User', avatarSizeStyle.fontSize, {
                    width: avatarSizeStyle.width,
                    height: avatarSizeStyle.height,
                    cursor: enableDefaultNavigation ? 'pointer' : 'inherit',
                    ...sx,
                })}>
                {/* Keep explicit children so your previous behavior (two big letters) remains */}
                {upperCaseFirstName?.charAt(0)} {upperCaseLastName?.charAt(0)}
            </Avatar>
        </Badge>
    );
};

export { ThspianAvatar };
